/**
 * Infiltration Run — static data types, career tiers, tutorial config.
 * Event definitions live in ./infiltrationEvents.ts (re-exported below).
 * Pure data only — no DOM, no Math.random.
 */

/** Which agent stat a choice tests. 'none' = no roll (always succeeds, 0 detection). */
export type InfilStatType = 'disguise' | 'combat' | 'hacking' | 'charisma' | 'none';

/** Risk category — drives UI color and career ambition weight. */
export type InfilTag = 'safe' | 'risky' | 'aggressive';

export interface InfilChoice {
  id: string;
  labelKey: string;
  descKey: string;
  statType: InfilStatType;      // 'none' = no judgement (safe)
  baseSuccess: number;          // 0..100 own base success rate
  difficulty: number;           // 10..90 (ignored when statType is 'none')
  detectionOnSuccess: number;   // detection added on success
  detectionOnFail: number;      // detection added on failure
  rewardIp: number;             // IP granted on success (0 allowed)
  rewardDocs: number;           // classified docs granted on success
  tag: InfilTag;                // UI color / ambition weight
}

export interface InfilEvent {
  id: string;
  titleKey: string;
  categoryKey: string;   // small grey category label above the title
  descKey: string;       // situation description
  imagePath: string;     // images/infil-xxx.png (placeholder until art exists)
  choices: InfilChoice[];
  minStage?: number;     // minimum infiltration stage to appear (difficulty curve)
}

/** Career tiers, lowest to highest. Higher tier requires surviving with more ambition. */
export type CareerTier =
  | 'cleaner'
  | 'guard'
  | 'clerk'
  | 'manager'
  | 'executive'
  | 'director'
  | 'warrior';

/** Ordered low -> high. Index used for ranking / display. */
export const CAREER_TIERS: CareerTier[] = [
  'cleaner',
  'guard',
  'clerk',
  'manager',
  'executive',
  'director',
  'warrior',
];

/** i18n key for a career tier name (accepts any tier id string). */
export function careerNameKey(tier: string): string {
  return `infil_career_${tier}`;
}

/** Rank index of a career tier (0 = lowest). Returns -1 if unknown. */
export function careerRank(tier: string | null): number {
  if (!tier) return -1;
  return CAREER_TIERS.indexOf(tier as CareerTier);
}

/** Higher of two career tiers (by rank). Used to track the player's best. */
export function higherCareer(a: string | null, b: string | null): string | null {
  return careerRank(a) >= careerRank(b) ? a : b;
}

/**
 * Fixed event sequence for the first (tutorial) run.
 * Teaches the "going all-in for the top tier backfires" lesson via scripted outcomes.
 */
export const TUTORIAL_EVENT_IDS: string[] = ['patrol', 'security_check', 'board_meeting'];

/** Fixed agent stats used during the tutorial run (predictable success rates). */
export const TUTORIAL_STATS = { disguise: 12, combat: 8, hacking: 10, charisma: 10 };

/** Number of events per normal run (randomly 5 or 6). */
export const RUN_EVENT_MIN = 5;
export const RUN_EVENT_MAX = 6;

export { INFIL_EVENTS } from './infiltrationEvents.js';
import { INFIL_EVENTS } from './infiltrationEvents.js';

/** Look up an event definition by id. */
export function getInfilEvent(id: string): InfilEvent | undefined {
  return INFIL_EVENTS.find((e) => e.id === id);
}

/** Look up a choice within an event by choice id. */
export function getInfilChoice(eventId: string, choiceId: string): InfilChoice | undefined {
  return getInfilEvent(eventId)?.choices.find((c) => c.id === choiceId);
}
